import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

function defaultState(row) {
  if (row) {
    return {
      username: row.username,
      fullname: row.fullname ?? '',
      role: row.role,
      password: '' // only set = reset password
    };
  }
  return { username: '', fullname: '', role: 'user', password: '' };
}

export default function UserForm({ editing, onClose, onSaved, currentUserId }) {
  const [form, setForm] = useState(() => defaultState(editing));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const isSelf = editing && editing.id === currentUserId;

  async function submit(ev) {
    ev.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          fullname: form.fullname,
          role: form.role
        };
        if (form.password) payload.password = form.password;
        const saved = await api.patch(`/users/${editing.id}`, payload);
        onSaved(saved, { isNew: false });
      } else {
        const saved = await api.post('/users', {
          username: form.username.trim(),
          password: form.password,
          fullname: form.fullname,
          role: form.role
        });
        onSaved(saved, { isNew: true });
      }
    } catch (err) {
      setError(err?.data?.error ?? err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-end bg-ink-900/40 p-0" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-100 px-6 py-4">
          <h3 className="text-base font-semibold text-ink-900">
            {editing ? `Edit user #${editing.id}` : 'New user'}
          </h3>
          <button className="btn-ghost p-2" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
        </div>

        <form onSubmit={submit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <Field label="Username" required>
              <input
                className="input"
                value={form.username}
                onChange={(e) => update({ username: e.target.value })}
                required
                disabled={!!editing}
                autoFocus={!editing}
              />
            </Field>

            <Field label="Full name">
              <input className="input" value={form.fullname} onChange={(e) => update({ fullname: e.target.value })} />
            </Field>

            <Field label="Role" required>
              <select
                className="input"
                value={form.role}
                onChange={(e) => update({ role: e.target.value })}
                disabled={isSelf}
              >
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
              {isSelf && <div className="mt-1 text-xs text-ink-500">You cannot change your own role.</div>}
            </Field>

            <Field
              label={editing ? 'Reset password' : 'Password'}
              required={!editing}
              hint={
                editing
                  ? 'Leave blank to keep current password. If set: 10+ chars, 3 of 4 classes (lower/upper/digit/symbol), not username or a common word.'
                  : 'At least 10 characters. Mix at least 3 of: lowercase, uppercase, digit, symbol. Avoid username and common words.'
              }
            >
              <input
                className="input"
                type="password"
                value={form.password}
                onChange={(e) => update({ password: e.target.value })}
                required={!editing}
                minLength={editing ? 0 : 10}
                autoComplete="new-password"
              />
            </Field>

            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-ink-100 px-6 py-3">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? <><i className="fa-solid fa-spinner fa-spin-pulse" /> Saving…</> : (editing ? 'Save changes' : 'Create user')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-semibold text-ink-800">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <div className="mt-1 text-xs text-ink-500">{hint}</div>}
    </div>
  );
}
